import { Section } from '@/_shared/components/section/index.js';
import { useTsRest } from '@/_shared/context/ts-rest.js';
import { Button, Divider, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowLeftRegular, ArrowRightRegular, DocumentRegular } from '@fluentui/react-icons';
import { px } from '@this/app-shared/dist/tools/styles.js';
import type { GetDocumentResponse } from '@this/shared/dist/apis/serviceaddinapi/documents.js';
import type { GetPaymentDecisionDocumentResponse } from '@this/shared/dist/apis/serviceaddinapi/payment-decision-document.js';
import { describeUnavailableContent } from '@this/shared/dist/data/document-content.js';
import { fromIso, toHumanReadable } from '@this/shared/dist/tools/datetime.js';
import { upgradePlainTextToHtml } from '@this/shared/dist/tools/html.js';
import { anonymizeIban } from '@this/shared/dist/tools/iban.js';
import {
    formatEur,
    formatNumber,
    formatPreciseEur,
    formatPreciseNumber,
} from '@this/shared/dist/tools/number-format.js';
import { encode } from 'html-entities';
import { useCallback, useEffect, useState } from 'react';
import { Letter } from 'react-letter';

type DocumentViewerProps = {
    documentIds: string[] | null;
};

type PaymentDecisionDocument = GetPaymentDecisionDocumentResponse['document'];

// Helper function to render payment decision documents
function renderPaymentDecisionDocument(
    document: PaymentDecisionDocument,
    approvedByAi: boolean,
    styles: ReturnType<typeof useStyles>
) {
    const documentTypeMap = {
        invoice: 'Rechnung',
        quote: 'Kostenvoranschlag',
        'self-repair': 'Eigenleistung',
    };

    return (
        <div className={styles.paymentDecisionContainer}>
            <div className={styles.paymentDecisionHeader}>
                <strong>
                    Beleg: {documentTypeMap[document.documentType]}
                    {document.documentNumber ? ` ${document.documentNumber}` : ''}
                    {document.businessName ? ` (${document.businessName})` : ''}
                </strong>
            </div>

            <div className={styles.paymentDecisionSection}>
                <p>
                    {(() => {
                        if (
                            document.decision === 'denial' ||
                            (document.decision === 'settlement' && document.payoutEur <= 0) ||
                            (document.decision === 'authorization' && document.totalAuthorizationEur <= 0)
                        ) {
                            return 'Bitte haben Sie Verständnis, dass wir bedingungsgemäß keine Regulierung des Belegs vornehmen können.';
                        }

                        if (document.decision === 'authorization') {
                            let verdict = `Wir geben den nachfolgenden Betrag${document.provisional ? ' unter Vorbehalt' : ''} frei, so dass Sie die entsprechenden Leistungen veranlassen können.`;

                            if (document.provisional) {
                                // An AI release is provisional by rule; a clerk's reservation still needs input
                                verdict += approvedByAi
                                    ? ' Die Auszahlung erfolgt nach Prüfung der eingereichten Rechnung.'
                                    : ' Wir benötigen für eine Auszahlung neben der Rechnung noch weitere Informationen von Ihnen.';
                            }

                            if (document.appliedDeductibleEur > 0) {
                                verdict += ' Bitte beachten Sie, ';
                                if (document.appliedDeductibleEur < document.totalAuthorizationEur) {
                                    verdict +=
                                        'dass voraussichtlich bei Einreichung der Rechnung der Auszahlungsbetrag um den Selbstbehalt gemindert wird.';
                                } else {
                                    verdict +=
                                        'dass es bei Einreichung einer Rechnung voraussichtlich aufgrund der Minderung durch den Selbstbehalt zu keiner Auszahlung kommen wird.';
                                }
                            }

                            return verdict;
                        }

                        if (document.decision === 'settlement') {
                            // A payout KIM makes on its own carries the insurer's reservation of rights
                            return approvedByAi
                                ? 'Wir erstatten den nachfolgenden Betrag zu den erbrachten Leistungen. Die Zahlung erfolgt ohne Anerkennung einer Rechts- und Zahlungspflicht und ohne Präjudiz für diesen und zukünftige Schadenfälle.'
                                : 'Wir erstatten den nachfolgenden Betrag zu den erbrachten Leistungen.';
                        }

                        return '';
                    })()}
                </p>
                {document.paymentReason && (
                    <p
                        dangerouslySetInnerHTML={{
                            __html: upgradePlainTextToHtml(document.paymentReason),
                        }}
                    />
                )}
            </div>

            <div className={styles.paymentDecisionSection}>
                <div className={styles.paymentDecisionSectionLabel}>Leistungspositionen</div>
                <table className={styles.paymentDecisionTable}>
                    <thead>
                        <tr>
                            <th>Beschreibung</th>
                            <th>Preis</th>
                            <th>Anzahl</th>
                            <th>Gesamt</th>
                            <th>Steuer</th>
                            <th>Basis</th>
                            {(document.decision === 'authorization' || document.decision === 'settlement') && (
                                <th>Kürzung</th>
                            )}
                            <th>
                                {
                                    {
                                        authorization: 'Freigabe',
                                        settlement: 'Erstattung',
                                        denial: 'Ablehnung',
                                    }[document.decision]
                                }
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {document.lineItems.map((lineItem, lineIndex) => (
                            <tr key={lineIndex}>
                                <td>{lineItem.description || '-'}</td>
                                <td>
                                    {lineItem.priceNetEur !== null ? formatPreciseEur(lineItem.priceNetEur) : '-'}
                                    <br />
                                    {lineItem.priceGrossEur !== null ? formatPreciseEur(lineItem.priceGrossEur) : '-'}
                                </td>
                                <td>
                                    {lineItem.quantity !== null ? formatPreciseNumber(lineItem.quantity) : '-'}
                                    <br />
                                    {lineItem.unit || '-'}
                                </td>
                                <td>
                                    {formatEur(lineItem.amountNetEur)}
                                    <br />
                                    {formatEur(lineItem.amountGrossEur)}
                                </td>
                                <td>
                                    {formatNumber(lineItem.taxPercentage, 1)}%<br />
                                    {formatEur(lineItem.taxEur)}
                                </td>
                                <td>
                                    {
                                        {
                                            net: 'Netto',
                                            gross: 'Brutto',
                                        }[lineItem.calculationBasis]
                                    }
                                </td>
                                {document.decision === 'authorization' || document.decision === 'settlement' ? (
                                    <td>
                                        {formatNumber(
                                            'deductionPercentage' in lineItem ? lineItem.deductionPercentage : 0,
                                            1
                                        )}
                                        %
                                        <br />
                                        {formatEur('deductionEur' in lineItem ? lineItem.deductionEur : 0)}
                                    </td>
                                ) : null}
                                <td>
                                    {document.decision === 'authorization'
                                        ? formatEur('authorizationEur' in lineItem ? lineItem.authorizationEur : 0)
                                        : null}
                                    {document.decision === 'settlement'
                                        ? formatEur('settlementEur' in lineItem ? lineItem.settlementEur : 0)
                                        : null}
                                    {document.decision === 'denial'
                                        ? formatEur('denialEur' in lineItem ? lineItem.denialEur : 0)
                                        : null}
                                </td>
                            </tr>
                        ))}
                        {document.lineItems.length > 1 ? (
                            <tr>
                                <td>Gesamt</td>
                                <td></td>
                                <td></td>
                                <td>
                                    {formatEur(document.totalAmountNetEur)}
                                    <br />
                                    {formatEur(document.totalAmountGrossEur)}
                                </td>
                                <td>{formatEur(document.totalTaxEur)}</td>
                                <td></td>
                                {document.decision === 'authorization' || document.decision === 'settlement' ? (
                                    <td>{formatEur(document.totalDeductionEur)}</td>
                                ) : null}
                                <td>
                                    {document.decision === 'authorization'
                                        ? formatEur(document.totalAuthorizationEur)
                                        : null}
                                    {document.decision === 'settlement' ? formatEur(document.totalSettlementEur) : null}
                                    {document.decision === 'denial' ? formatEur(document.totalDenialEur) : null}
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>

            {document.decision === 'authorization' || document.decision === 'settlement' ? (
                <div className={styles.paymentDecisionSummary}>
                    <div className={styles.paymentDecisionSummaryRow}>
                        <span>Summe:&nbsp;</span>
                        <span>
                            {document.decision === 'authorization' ? formatEur(document.totalAuthorizationEur) : null}
                            {document.decision === 'settlement' ? formatEur(document.totalSettlementEur) : null}
                        </span>
                    </div>

                    <div className={styles.paymentDecisionSummaryRow}>
                        <span>Abzug Selbstbehalt:&nbsp;</span>
                        <span>{formatEur(document.appliedDeductibleEur)}</span>
                    </div>

                    {document.decision === 'settlement' ? (
                        <div className={styles.paymentDecisionSummaryRow}>
                            <span>Auszahlung:&nbsp;</span>
                            <span>{formatEur(document.payoutEur)}</span>
                        </div>
                    ) : null}

                    {document.decision === 'settlement' && document.payoutEur > 0 ? (
                        <>
                            <div
                                className={styles.paymentDecisionSummaryRow}
                                style={{ marginTop: tokens.spacingVerticalS }}
                            >
                                <span>Zahlungsempfänger:&nbsp;</span>
                                <span>
                                    {document.bankAccount?.holderName ||
                                        document.bankAccount?.personName ||
                                        document.bankAccountFeedback?.accountHolder ||
                                        '-'}
                                </span>
                            </div>
                            <div className={styles.paymentDecisionSummaryRow}>
                                <span>IBAN:&nbsp;</span>
                                <span style={{ fontFamily: 'monospace' }}>
                                    {anonymizeIban(
                                        document.bankAccount?.accountIban || document.bankAccountFeedback?.iban || null
                                    ) || '-'}
                                </span>
                            </div>
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

// Helper function to render different document types
function renderDocument(document: GetDocumentResponse, styles: ReturnType<typeof useStyles>) {
    const mimeType = document.mimeType;

    // Content that was never imported: there is nothing to load, only the reason to show.
    if (document.contentUnavailable) {
        return (
            <div className={styles.emptyState}>
                <p>Inhalt nicht verfügbar</p>
                <p style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
                    {describeUnavailableContent(document.contentUnavailable)}, nicht importiert.
                </p>
            </div>
        );
    }

    // Email documents
    if ((mimeType === 'message/rfc822' || mimeType === 'application/vnd.ms-outlook') && document.emailData) {
        return (
            <div className={styles.emailContainer}>
                <div className={styles.emailHeader}>
                    {!!document.emailData.from && (
                        <>
                            <div className={styles.emailHeaderLabel}>Von:</div>
                            <div>{document.emailData.from}</div>
                        </>
                    )}

                    {!!document.emailData.to?.length && (
                        <>
                            <div className={styles.emailHeaderLabel}>An:</div>
                            <div>
                                {document.emailData.to.map((recipient, index) => (
                                    <div key={index}>{recipient}</div>
                                ))}
                            </div>
                        </>
                    )}

                    {!!document.emailData.cc?.length && (
                        <>
                            <div className={styles.emailHeaderLabel}>CC:</div>
                            <div>
                                {document.emailData.cc.map((recipient, index) => (
                                    <div key={index}>{recipient}</div>
                                ))}
                            </div>
                        </>
                    )}

                    {!!document.emailData.subject && (
                        <>
                            <div className={styles.emailHeaderLabel}>Betreff:</div>
                            <div>{document.emailData.subject}</div>
                        </>
                    )}

                    {!!document.emailData.date && (
                        <>
                            <div className={styles.emailHeaderLabel}>Gesendet:</div>
                            <div>{toHumanReadable(fromIso(document.emailData.date), 'datetime_short_2digit')}</div>
                        </>
                    )}
                </div>

                <Divider />

                {document.emailData.body.html ? (
                    <div className={styles.emailContent}>
                        <Letter
                            html={document.emailData.body.html}
                            text={document.emailData.body.text ?? undefined}
                            allowedSchemas={['http', 'https', 'mailto', 'data']}
                        />
                    </div>
                ) : (
                    <div
                        className={styles.emailContent}
                        dangerouslySetInnerHTML={{
                            __html: document.emailData.body.text
                                ? encode(document.emailData.body.text).replaceAll('\n', '<br />')
                                : 'Kein Inhalt',
                        }}
                    />
                )}
            </div>
        );
    }

    // Image documents
    if (mimeType.startsWith('image/')) {
        return <img src={document.url} alt="Dokument" className={styles.image} />;
    }

    // PDF documents
    if (mimeType === 'application/pdf') {
        return <iframe className={styles.iframe} src={document.url} title="Dokument" />;
    }

    // Office documents with Office web viewer
    if (
        mimeType.includes('application/msword') ||
        mimeType.includes('application/vnd.openxmlformats-officedocument') ||
        mimeType.includes('application/vnd.ms-excel') ||
        mimeType.includes('application/vnd.ms-powerpoint') ||
        mimeType.includes('application/vnd.oasis.opendocument')
    ) {
        return (
            <iframe
                className={styles.iframe}
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(document.url)}`}
                title="Dokument"
            />
        );
    }

    // Video documents
    if (mimeType.startsWith('video/')) {
        return (
            <video controls className={styles.iframe} style={{ backgroundColor: tokens.colorNeutralBackground1 }}>
                <source src={document.url} type={mimeType} />
                Ihr Browser unterstützt dieses Videoformat nicht.
            </video>
        );
    }

    // Unsupported file types
    return (
        <div className={styles.emptyState}>
            <p>Dateityp wird nicht unterstützt</p>
            <p style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>{mimeType}</p>
            <a href={document.url} target="_blank" rel="noopener noreferrer">
                <Button appearance="primary">Datei herunterladen</Button>
            </a>
        </div>
    );
}

const useStyles = makeStyles({
    container: {
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        position: 'relative',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground3,
    },
    paymentDecisionContainer: {
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusLarge,
        boxShadow: tokens.shadow4,
        padding: tokens.spacingHorizontalXXL,
    },
    paymentDecisionHeader: {
        fontSize: tokens.fontSizeBase400,
        marginBottom: tokens.spacingVerticalL,
    },
    paymentDecisionSection: {
        marginBottom: tokens.spacingVerticalL,
        '& p': {
            fontSize: tokens.fontSizeBase300,
            lineHeight: tokens.lineHeightBase300,
            marginTop: px(0),
            marginBottom: tokens.spacingVerticalS,
        },
    },
    paymentDecisionSectionLabel: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: tokens.spacingVerticalS,
    },
    paymentDecisionTable: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: tokens.fontSizeBase200,
        marginTop: tokens.spacingVerticalS,
        '& th, & td': {
            border: `1px solid ${tokens.colorNeutralStroke1}`,
            padding: tokens.spacingHorizontalS,
            textAlign: 'center',
        },
        '& th': {
            backgroundColor: tokens.colorNeutralBackground3,
            fontWeight: tokens.fontWeightSemibold,
            whiteSpace: 'nowrap',
        },
        '& td:first-child': {
            textAlign: 'left',
            wordBreak: 'break-word',
        },
    },
    paymentDecisionSummary: {
        backgroundColor: tokens.colorNeutralBackground3,
        padding: tokens.spacingHorizontalL,
        borderRadius: tokens.borderRadiusMedium,
    },
    paymentDecisionSummaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: tokens.fontSizeBase300,
        marginBottom: tokens.spacingVerticalS,
        '&:last-child': {
            marginBottom: px(0),
        },
    },
    navigation: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: tokens.spacingVerticalM,
    },
    documentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXL,
        minHeight: 'min-content',
    },
    iframe: {
        width: '100%',
        height: '80vh',
        border: 'none',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusLarge,
        boxShadow: tokens.shadow4,
    },
    emailContainer: {
        display: 'grid',
        gridTemplateRows: 'repeat(2, max-content) 1fr',
        height: 'max-content',
        width: '100%',
        minHeight: 'min-content',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusLarge,
        boxShadow: tokens.shadow4,
        '& > * table': {
            margin: `${px(0)} !important`,
            padding: `${px(0)} !important`,
        },
    },
    emailHeader: {
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        flexDirection: 'column',
        columnGap: tokens.spacingVerticalM,
        padding: tokens.spacingHorizontalXXL,
        '& > *': {
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
        },
    },
    emailHeaderLabel: {
        fontWeight: tokens.fontWeightMedium,
    },
    emailContent: {
        height: '100%',
        minHeight: 'min-content',
        padding: tokens.spacingHorizontalXXL,
        '& *': {
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
        },
        '&:has(img) *': {
            maxHeight: 'max-content',
            '& img': {
                maxWidth: '100%',
                objectFit: 'contain',
            },
        },
    },
    image: {
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusLarge,
        boxShadow: tokens.shadow4,
    },
});

export function DocumentViewer({ documentIds }: DocumentViewerProps) {
    const styles = useStyles();
    const tsr = useTsRest();
    const [activeIndex, setActiveIndex] = useState(0);

    // Reset active index when documentIds change
    // biome-ignore lint/correctness/useExhaustiveDependencies: We want to reset on documentIds array change
    useEffect(() => {
        setActiveIndex(0);
    }, [documentIds]);

    const currentDocumentId = documentIds?.[activeIndex];

    // Check if this is a virtual payment decision document
    const isPaymentDecisionDocument = currentDocumentId?.startsWith('virtual-payment-decision-document:');
    const paymentDecisionParams = isPaymentDecisionDocument
        ? currentDocumentId!.replace('virtual-payment-decision-document:', '').split('/')
        : null;

    const { data: documentData, isLoading } = tsr.documents.getDocument.useQuery({
        queryKey: ['documents', 'getDocument', currentDocumentId],
        queryData: {
            params: {
                documentId: currentDocumentId!,
            },
        },
        enabled: !!currentDocumentId && !isPaymentDecisionDocument,
    });

    const { data: paymentDecisionData, isLoading: isLoadingPaymentDecision } =
        tsr.paymentDecisionDocument.getDocument.useQuery({
            queryKey: ['paymentDecisionDocument', 'getDocument', ...(paymentDecisionParams || [])],
            queryData: {
                params: {
                    paymentDecisionId: paymentDecisionParams?.[0] || '',
                    documentIndex: paymentDecisionParams?.[1] || '',
                },
            },
            enabled: !!paymentDecisionParams,
        });

    const handlePrevious = useCallback(() => {
        setActiveIndex((prev) => Math.max(0, prev - 1));
    }, []);

    const handleNext = useCallback(() => {
        setActiveIndex((prev) => Math.min((documentIds?.length ?? 1) - 1, prev + 1));
    }, [documentIds]);

    if (!documentIds || documentIds.length === 0) {
        return (
            <div className={styles.container}>
                <Section
                    fillContainer
                    removeContentPadding
                    header={{
                        label: 'Dokumentenansicht',
                        color: 'default',
                        icon: <DocumentRegular />,
                    }}
                >
                    <div className={styles.emptyState}>
                        <p>Kein Dokument ausgewählt</p>
                    </div>
                </Section>
            </div>
        );
    }

    const hasNavigation = documentIds.length > 1;

    return (
        <div className={styles.container}>
            <Section
                fillContainer
                header={{
                    label: 'Dokumentenansicht',
                    color: 'default',
                    icon: <DocumentRegular />,
                }}
                childrenArray={[
                    ...(hasNavigation
                        ? [
                              {
                                  removeContentPadding: true,
                                  size: 'max-content' as const,
                                  element: (
                                      <div className={styles.navigation}>
                                          <Button
                                              appearance="subtle"
                                              icon={<ArrowLeftRegular />}
                                              onClick={handlePrevious}
                                              disabled={activeIndex === 0}
                                          />
                                          <span>
                                              {activeIndex + 1} / {documentIds.length}
                                          </span>
                                          <Button
                                              appearance="subtle"
                                              icon={<ArrowRightRegular />}
                                              onClick={handleNext}
                                              disabled={activeIndex === documentIds.length - 1}
                                          />
                                      </div>
                                  ),
                              },
                          ]
                        : []),
                    {
                        removeContentPadding: false,
                        showGradient: true,
                        element: (
                            <div className={styles.documentWrapper}>
                                {isLoading || isLoadingPaymentDecision ? (
                                    <div className={styles.emptyState}>
                                        <Spinner size="small" label="Dokument wird geladen..." />
                                    </div>
                                ) : isPaymentDecisionDocument && paymentDecisionData?.body ? (
                                    <>
                                        {renderPaymentDecisionDocument(
                                            paymentDecisionData.body.document,
                                            paymentDecisionData.body.approvedByAi,
                                            styles
                                        )}
                                    </>
                                ) : documentData?.body ? (
                                    <>{renderDocument(documentData.body, styles)}</>
                                ) : (
                                    <div className={styles.emptyState}>
                                        <p>Dokument konnte nicht geladen werden</p>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
}
